import { Button, Spinner, TextInput } from '@mcgames/ui';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router';
import { useSession } from '../api/auth-store.ts';
import { useErrorText, useLogin, useVerifyMfa } from '../api/hooks.ts';
import { PublicLayout } from '../layout/PublicLayout.tsx';
import { ForbiddenPage } from './ForbiddenPage.tsx';
import styles from './Page.module.css';

/**
 * Username and password, then the MFA code when the account has one. Once a session exists the page
 * itself moves on: admins to the games list, everyone else to the forbidden page.
 */
export function LoginPage() {
  const session = useSession();
  if (session.status === 'restoring') {
    return (
      <PublicLayout>
        <Spinner />
      </PublicLayout>
    );
  }
  if (session.status === 'authenticated') {
    return session.user.role === 'admin' ? (
      <Navigate to="/admin/games" replace />
    ) : (
      <ForbiddenPage />
    );
  }
  return <LoginForms />;
}

function LoginForms() {
  const { t } = useTranslation('admin');
  const errorText = useErrorText();
  const login = useLogin();
  const verifyMfa = useVerifyMfa();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [code, setCode] = useState('');

  function submitCredentials(event: FormEvent) {
    event.preventDefault();
    login.mutate(
      { username: username.trim().toLowerCase(), password },
      {
        onSuccess: (response) => {
          if (response.status === 'mfa_required') {
            setMfaToken(response.mfaToken);
            setCode('');
            setPassword('');
          }
        },
      },
    );
  }

  function submitCode(event: FormEvent) {
    event.preventDefault();
    if (mfaToken !== null) verifyMfa.mutate({ mfaToken, code: code.trim() });
  }

  if (mfaToken !== null) {
    return (
      <PublicLayout>
        <form className={`${styles['card']} ${styles['form']}`} onSubmit={submitCode}>
          <h1 className={styles['title']}>{t('login.mfaTitle')}</h1>
          <p className={styles['text']}>{t('login.mfaHint')}</p>
          <TextInput
            label={t('login.mfaCode')}
            value={code}
            onChange={(event) => setCode(event.target.value)}
            autoComplete="one-time-code"
            autoCapitalize="none"
            spellCheck={false}
            required
            autoFocus
          />
          {verifyMfa.isError && (
            <p className={styles['error']} role="alert">
              {errorText(verifyMfa.error)}
            </p>
          )}
          <div className={styles['actions']}>
            <Button type="submit" disabled={verifyMfa.isPending || code.trim() === ''}>
              {t('login.mfaSubmit')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setMfaToken(null);
                verifyMfa.reset();
                login.reset();
              }}
            >
              {t('login.mfaBack')}
            </Button>
          </div>
        </form>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <form className={`${styles['card']} ${styles['form']}`} onSubmit={submitCredentials}>
        <h1 className={styles['title']}>{t('login.title')}</h1>
        <TextInput
          label={t('login.username')}
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          required
        />
        <TextInput
          label={t('login.password')}
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="current-password"
          required
        />
        {login.isError && (
          <p className={styles['error']} role="alert">
            {errorText(login.error)}
          </p>
        )}
        <div className={styles['actions']}>
          <Button type="submit" disabled={login.isPending}>
            {t('login.submit')}
          </Button>
        </div>
      </form>
    </PublicLayout>
  );
}
